import { newAuthService } from '@trp-ta-nitro/auth-browser-js';
import { ServiceError } from './services';
import config from '../../config';
import {
  authenticateUser,
  getEnablementStatusService,
  registerAndLoginUserService,
  getUserValidationStatusService
} from './services/user-service';
import { getRDFRecommendation } from './services/rdf-service';
import { postConsentAdvice } from './services/advice-service';
import { postReferrer } from './services/referrer-service';
import { postAddressValidation } from './services/address-validation-service';
import { postConsent } from './services/consent-service';
import { getCustodiansService } from './services/custodian';
import {
  getAddtionalCustomersExistingAgreementsService,
  getExistingAgreementsService
} from './services/existing-agreements-service';
import { getPlaidLinkToken } from './services/external-financial-institution-service';
import {
  loadRDFTerms,
  loadElectronicAccessTerms,
  loadFiduciaryDisclosureTerms,
  loadElectronicAccessDeliveryTerms,
  loadAEMTermsContent
} from './services/terms-service';
import { isRDFPath, isSwitchToCheck } from './testers';
import {
  applicationStatusTypes,
  parsePhone,
  PhoneNumberType,
  getOriginalResidentialAddress,
  getCorrectedResidentialAddress,
  getOriginalMailingAddress,
  getCorrectedMailingAddress,
  setCustomerValidationPayload,
  accountTypes,
  investmentMethods,
  fundingTypes,
  chargeTypes,
  setJointOwnersValidationPayload,
  parseCustomerInformation,
  setDefaultedProfileData,
  filterExistingInvestmentsFromNew,
  filterNewInvestmentsFromExisting,
  isTaxableTransfer
} from './utils';
import { formatDate } from './dateUtils';
import { unlockRewards, parseIClassFunds } from './summit.utils';
import { meetsSummitIClassMin } from './summit.testers';
import { DataLoader } from '../../core/types/data-loader';
import {
  merge,
  withDefault,
  parseAmount,
  removeCommasAndParseFloat,
  currentRouteToHash,
  isEmpty
} from '../../core/util';
import { analyticsDataLayer } from '../../analytics/analytics-helper';
import {
  ApplicationState,
  StateStore,
  PartialStateStore,
  StateStoreOrVoid
} from '../../core/types/application-state';
import { createNewReturnJourneyProfile } from '../return-journey/loaders';
import {
  getExistingAgreementID,
  mapUpdatedInvestments
} from '../rollover/util';
import { retrieveFilterAndAssignAppID } from './services/profile-service';
import { submissionTrackingService } from './services/submission-tracking-service';
import { isMailingAddressDifferent } from '../front-office/shared/utils';
import { parseTaxableTransferProfileAccountType } from '../taxable-flows/shared/util';
import { getMoneyMarketFunds } from '../../screens/MoneyMarketScreen/queries';
import { getRetirementDateFunds } from '../../screens/RDFTermsScreen/queries';
import { convertProspectCustomer } from './services/customer-service';

export const loadUser: DataLoader = async (
  appState: ApplicationState
): Promise<StateStore> => {
  const {
    authenticated,
    enabled,
    retailEnabled,
    prospect,
    customer,
    summitData
  } = await authenticateUser(appState.store, '');

  return {
    authenticated: Boolean(authenticated),
    enabled: Boolean(enabled),
    retailEnabled: Boolean(retailEnabled),
    prospect: Boolean(prospect),
    userStateLoaded: true,
    summitData,
    app: { customer },
    mailingAddressDifferent: isMailingAddressDifferent(customer),
    loadedData: {
      citizenShipPresent: Boolean(customer?.citizenship)
    }
  };
};

/* eslint-disable complexity */
export const getEnablementStatus: DataLoader = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs: { ssn, dob, firstName, lastName, middleName = '' },
    store: {
      enabled,
      accountType,
      app: { customer }
    }
  } = appState;

  const customerValidationPayload = setCustomerValidationPayload(
    firstName,
    lastName,
    middleName,
    ssn,
    dob,
    customer
  );

  const userValidation = await getUserValidationStatusService({
    firstName: customerValidationPayload.firstName,
    lastName: customerValidationPayload.lastName,
    taxID: enabled ? customer?.taxID : customerValidationPayload.taxID,
    dateOfBirth: formatDate(customerValidationPayload.dob, 'MM/DD/YYYY', '')
  });

  const jointOwnersValidations: any[] = [];
  if (accountType === accountTypes.JOINT) {
    const {
      inputs: { people },
      store: {
        app: { jointOwners }
      }
    } = appState;
    const jointOwnersValidationPayload = setJointOwnersValidationPayload(
      people,
      jointOwners
    );

    jointOwnersValidationPayload.map(async jo => {
      const validationRes = await getUserValidationStatusService({
        firstName: jo.firstName,
        lastName: jo.lastName,
        taxID: jo.taxID,
        dateOfBirth: jo.dob
      });

      jointOwnersValidations.push(validationRes);
    });
  }

  if (userValidation?.validationStatus === 'INVALID') {
    return {
      app: {},
      loadedData: userValidation
    };
  }

  const userstatus = await getEnablementStatusService({
    firstName: customerValidationPayload.firstName,
    lastName: customerValidationPayload.lastName,
    taxID: enabled ? customer?.taxID : customerValidationPayload.taxID,
    dateOfBirth: formatDate(customerValidationPayload.dob, 'MM/DD/YYYY', '')
  });

  return {
    app: {},
    enabled: withDefault(userstatus?.enabled, enabled),
    loadedData: {
      ...userstatus,
      ...userValidation,
      ...(jointOwnersValidations?.length && { jointOwnersValidations })
    }
  };
};
/* eslint-enable complexity */

export const getUserExistingAgreements = async (
  userResults: any,
  agreementTypes: string[]
): Promise<any[]> => {
  if (isEmpty(agreementTypes)) {
    return [];
  }
  const existingAgreementsResults =
    userResults?.authenticated &&
    (await getExistingAgreementsService(agreementTypes));

  return existingAgreementsResults;
};

export const getAdditionalCustomersExistingAgreements = async ({
  authenticated,
  taxID,
  accountType
}): Promise<any[]> => {
  if (isEmpty(accountType)) {
    return [];
  }

  const additionalCustomerExistingAgreementspayload = {
    agreementTypes: [accountType],
    productTypes: ['MUTUAL_FUND'],
    ...(accountType === accountTypes.UGMAUTMA && { minorTaxID: taxID }),
    ...(accountType === accountTypes.JOINT && { jointOwnersTaxIDs: taxID })
  };
  const existingAgreementsResults =
    authenticated &&
    (await getAddtionalCustomersExistingAgreementsService(
      additionalCustomerExistingAgreementspayload
    ));

  return existingAgreementsResults;
};

export const registerAndLoginUser: DataLoader = async ({
  store: {
    app: { customer },
    authenticated,
    loadedData
  },
  inputs: { username: loginID, password }
}) => {
  if (!authenticated) {
    try {
      const userDetails = await registerAndLoginUserService({
        firstName: customer?.name?.firstName,
        lastName: customer?.name?.lastName,
        emailAddress: customer?.primaryEmailAddress,
        loginID: withDefault(loginID, loadedData?.loginID),
        password: withDefault(password, loadedData?.password)
      });

      return {
        loadedData: {
          usernameTaken: false,
          newUserCreated: loadedData?.newUserCreated || !authenticated
        },
        authenticated: userDetails?.authenticated
      };
    } catch (err) {
      const usernameTaken = Boolean(
        err.toString().match(/Username already exists/)
      );
      if (usernameTaken) {
        return {
          loadedData: {
            usernameTaken,
            loginID: withDefault(loginID, loadedData?.loginID),
            password: withDefault(password, loadedData?.password)
          }
        };
      }

      // if the error has nothing to do with username being taken - rethrow
      throw new ServiceError(err);
    }
  }

  return {
    loadedData: {
      usernameTaken: false,
      newUserCreated: !authenticated
    },
    authenticated: true
  };
};

export const commitAdviceConsent = async (
  consentID,
  store
): Promise<StateStore> => {
  const {
    dob,
    amount,
    loadedData: { cusip, ticker, trpCode }
  } = store;
  return postConsentAdvice({
    consentID,
    adviceCriteria: {
      retirementAge: 65,
      amountToInvest: amount,
      dateOfBirth: dob
    },
    recommendation: {
      ticker,
      cusip,
      trpcode: trpCode
    }
  });
};

export const commitConsent = async (
  termsIDs: any,
  customerData: any
): Promise<any> => {
  const {
    name: { firstName, lastName },
    primaryEmailAddress
  } = customerData;
  // make sure terms ids are numbers, not string, to match endpoint validation
  const transformedTermIDs = termsIDs?.map(id => parseInt(id, 10));

  return postConsent({
    consentee: {
      firstName,
      lastName,
      emailAddress: primaryEmailAddress
    },
    didConsent: true,
    termsAndConditionsIDs: transformedTermIDs
  });
};

export const commitRDFTermsConsent: DataLoader = async ({
  store
}): Promise<StateStore> => {
  const {
    termsData,
    app: { customer }
  } = store;
  const rdfTermsID = termsData?.rdf?.rdfTermsID;
  const electronicAccessTermsID =
    termsData?.electronicAccess?.electronicAccessTermsID;
  const fiduciaryTermsID = termsData?.fiduciary?.fiduciaryTermsID;
  const consentTermsPayload = [
    rdfTermsID,
    electronicAccessTermsID,
    fiduciaryTermsID
  ];

  const consentID = await commitConsent(consentTermsPayload, customer);
  const termsDataWithConsentID = {
    ...termsData,
    consentID: consentID?.consentID
  };
  const consentTermsPayloadAsStrings = consentTermsPayload.map(id =>
    id?.toString()
  );

  return {
    app: {
      ...store?.app,
      termsIDs: consentTermsPayloadAsStrings
    },
    termsData: termsDataWithConsentID
  };
};

/* eslint-disable complexity */
// TODO: simplify complexity
export const commitProfileData: DataLoader = async ({
  chosen,
  inputs,
  store
}): Promise<StateStoreOrVoid> => {
  if (chosen === 'skipLogin') {
    return {};
  }

  const shoppingFunds = chosen;
  const { amount } = inputs;
  const { accountType: storeAccountType = '', chargeType, app } = store;
  const accountType = isTaxableTransfer(storeAccountType, chargeType)
    ? parseTaxableTransferProfileAccountType(storeAccountType, chargeType)
    : storeAccountType;
  // account for defaulted input values not persisting for customers with existing data
  const defaultedProfileData = setDefaultedProfileData(inputs, store);
  const existingProfileAppID = await retrieveFilterAndAssignAppID(
    defaultedProfileData.primaryEmailAddress,
    accountType
  );

  const storeToCommit = {
    accountType: storeAccountType,
    chargeType,
    // fallback to fetch amount value from store, incase of modal action
    // wiping out the input value
    amount: removeCommasAndParseFloat(withDefault(amount, store?.amount)),
    app: {
      customer: {
        name: {
          firstName: defaultedProfileData.firstName,
          lastName: defaultedProfileData.lastName
        },
        primaryEmailAddress: defaultedProfileData.primaryEmailAddress,
        dob: defaultedProfileData.dob
      },
      ...(chargeType === chargeTypes.TRANSFER && {
        fundingMethodType: fundingTypes.CHECK
      }),
      ...(storeAccountType === accountTypes.TRANSFER && {
        toIRAAccountType: app?.toIRAAccountType,
        fromIRAAccountType: app?.fromIRAAccountType
      })
    },
    ...(shoppingFunds && { loadedData: { shoppingFunds } })
  };

  if (!existingProfileAppID)
    return createNewReturnJourneyProfile(storeToCommit);

  return {
    ...storeToCommit,
    app: { ...storeToCommit.app, profileApplicationID: existingProfileAppID }
  };
};
/* eslint-enable complexity */

export const loadTermsForRDF: DataLoader = async (): Promise<StateStore> => {
  const rdfTerms = await loadRDFTerms();
  const electronicAccessTerms = await loadElectronicAccessTerms();
  const fiduciaryTerms = await loadFiduciaryDisclosureTerms();

  const allRetirementDateFundsData = await getRetirementDateFunds();

  const {
    termsID: rdfTermsID,
    termsAndConditionParagraphs: rdfTermsAndConditionParagraphs
  } = rdfTerms;
  const {
    termsID: electronicAccessTermsID,
    termsAndConditionParagraphs: electronicAccessTermsAndConditionParagraphs
  } = electronicAccessTerms;
  const {
    termsID: fiduciaryTermsID,
    termsAndConditionParagraphs: fiduciaryTermsAndConditionParagraphs
  } = fiduciaryTerms;

  return {
    app: {},
    termsData: {
      rdf: {
        rdfTermsID,
        rdfTermsAndConditionParagraphs
      },
      electronicAccess: {
        electronicAccessTermsID,
        electronicAccessTermsAndConditionParagraphs
      },
      fiduciary: {
        fiduciaryTermsID,
        fiduciaryTermsAndConditionParagraphs
      }
    },
    loadedData: {
      allRetirementDateFundsData
    }
  };
};

export const loadRDFiClassData: DataLoader = async ({
  store
}): Promise<StateStore> => {
  const { app, amount = 0 } = store;
  const dob = app?.customer?.dob;

  const iClassFundDetails = await getRDFRecommendation({
    birthDate: dob,
    amount,
    isSummitMember: true
  });
  const rdfTerms = await loadRDFTerms();
  const {
    termsID: rdfTermsID,
    termsAndConditionParagraphs: rdfTermsAndConditionParagraphs
  } = rdfTerms;

  return {
    app: {},
    loadedData: { iClassFundDetails },
    termsData: {
      rdf: {
        rdfTermsID,
        rdfTermsAndConditionParagraphs
      }
    }
  };
};

// TODO [2021-12-01]: fix complexity
/* eslint-disable complexity */
export const commitNewInvestmentsRDF: DataLoader = async ({
  store: {
    accountType,
    amount,
    app: { customer },
    termsData
  },
  chosen
}) => {
  // Bypasses commiting to store when not needed
  if (typeof chosen === 'string') return {};

  const { investmentMethod, fund } = chosen;
  const { ticker, trpCode } = fund;
  const dob = customer?.dob;

  const newInvestments = [
    {
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount: amount || 0,
        fundingAmountPercentage: 100
      }
    }
  ];

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  const { consentID } = termsData;
  const rdfCommitResults = {
    amount,
    app: { newInvestments, investmentMethod },
    ...(accountType === accountTypes.ROLLOVER && {
      estimatedRolloverAmount: amount
    }),
    loadedData: { rdfData: { ...fund } },
    chosenInvestments: [{ ...fund, amount, percent: 100 }]
  };

  if (consentID && fund.cusip) {
    await commitAdviceConsent(consentID, { dob, amount, loadedData: fund });
  }
  return rdfCommitResults;
};
/* eslint-enable complexity */

export const commitExistingInvestments: DataLoader = ({
  store: { accountType, amount = 0, app },
  chosen,
  result
}) => {
  // commit empty existingAccounts array to the store, if user didn't
  // select any existing funds and also incase user is coming back
  // from review page and removes all the existing funds that are
  // previously selected
  if (!chosen?.length) return { app: { existingAccounts: [] } };

  // builds all investment data
  const allInvestments = mapUpdatedInvestments(amount, app, chosen, result);

  analyticsDataLayer({
    quantity: allInvestments.existingInvestments.length,
    existingInvestments: allInvestments.existingInvestments
  });

  return {
    app: {
      newInvestments: withDefault(allInvestments.updatedNewInvestments, []),
      existingAccounts: allInvestments.existingInvestments,
      investmentMethod:
        app?.newInvestments && app?.newInvestments.length
          ? investmentMethods.MULTIPLE
          : investmentMethods.EXISTING
    },
    amount,
    ...(accountType === accountTypes.ROLLOVER && {
      estimatedRolloverAmount: amount
    }),
    chosenInvestments: allInvestments.updatedChosenInvestments
  };
};

export const getModalShownAndResult = (
  result = '',
  modalAlreadyShown = false
): any => {
  const modalShown: boolean =
    !modalAlreadyShown &&
    Boolean(result) &&
    (result === 'yesIClass' || result === 'noIClass');
  const updateToIClass = result && result === 'yesIClass';
  return { modalShown, updateToIClass };
};

// TODO [2021-12-01]: fix complexity
/* eslint-disable complexity */
export const commitNewInvestmentsBYOP: DataLoader = ({
  store,
  chosen,
  result
}) => {
  const { accountType, app } = store;

  const { modalShown, updateToIClass } = getModalShownAndResult(
    result,
    store?.updateToIClassModalShown
  );

  const filteredNewInvestments = filterExistingInvestmentsFromNew(
    app?.existingAccounts,
    chosen
  );
  const filteredExistingsInvestments = filterNewInvestmentsFromExisting(
    app?.existingAccounts,
    chosen
  );

  const parsedFilteredNewInvestments = parseIClassFunds(
    filteredNewInvestments,
    updateToIClass,
    store?.summitData
  );

  const updatedExistingAccounts = filteredExistingsInvestments.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage
    }) => ({
      investmentVehicle: {
        ticker,
        trpCode
      },
      agreementID: getExistingAgreementID(ticker, app),
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      }
    })
  );
  const newInvestments = parsedFilteredNewInvestments.map(
    ({
      ticker,
      trpCode,
      amount: fundingAmount,
      percent: fundingAmountPercentage,
      unlockedFund
    }) => ({
      ticker,
      trpCode,
      fundingAmount,
      fundingDetails: {
        fundingAmount,
        fundingAmountPercentage
      },
      unlockedFund: updateToIClass || unlockedFund
    })
  );

  const totalAmount =
    chosen?.length > 0
      ? parseAmount(
          chosen
            .reduce((accumulator, { amount }) => {
              return accumulator + parseFloat(amount);
            }, 0)
            .toFixed(2)
        )
      : store?.amount;

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    app: {
      existingAccounts: updatedExistingAccounts,
      newInvestments,
      investmentMethod:
        app?.existingAccounts && app.existingAccounts?.length
          ? investmentMethods.MULTIPLE
          : investmentMethods.BYOP
    },
    amount: totalAmount,
    ...(accountType === accountTypes.ROLLOVER && {
      estimatedRolloverAmount: totalAmount
    }),
    chosenInvestments: [
      ...filteredExistingsInvestments,
      ...parsedFilteredNewInvestments
    ],
    updateToIClassModalShown: modalShown,
    ...(store.chargeType === chargeTypes.TRANSFER && {
      fundingMethodType: fundingTypes.CHECK
    })
  };
};
/* eslint-enable complexity */

export const commitFinalizedInvestments: DataLoader = appState => {
  const {
    store: { app }
  } = appState;

  if (app?.investmentMethod === investmentMethods.EXISTING)
    return commitExistingInvestments(appState);
  if (app?.investmentMethod === investmentMethods.RDF)
    return commitNewInvestmentsRDF(appState);
  return commitNewInvestmentsBYOP(appState);
};

/* eslint-disable */
export const commitPersonalInformation = async (
  appState
): Promise<PartialStateStore> => {
  const {
    inputs,
    store: {
      enabled,
      amount,
      app: { customer, termsIDs },
      termsData,
      summitData: { assetTierID = 0 } = {},
      loadedData: { rdfData = {} } = {}
    },
    chosen
  } = appState;

  const dob = customer?.dob;

  let res;

  if (Array.isArray(chosen)) {
    res = commitNewInvestmentsBYOP(appState);
    return res;
  }

  if (chosen === 'modal-update') {
    // use the consentID already saved for the session, or refetch if missing (i.e. RJ)
    const consentTerms = termsIDs?.map(id => parseInt(id, 10));
    const consentID =
      termsData?.consentID ||
      (await commitConsent(consentTerms, customer))?.consentID;
    await commitAdviceConsent(consentID, { dob, amount, loadedData: rdfData });

    const iClassFundDetails = await getRDFRecommendation({
      birthDate: dob,
      amount,
      isSummitMember: true
    });

    res = {
      app: {
        newInvestments: [
          {
            ticker: rdfData.ticker,
            trpCode: rdfData.trpCode,
            fundingDetails: {
              fundingAmount: amount,
              fundingAmountPercentage: 100
            }
          }
        ]
      },
      chosenInvestments: [
        {
          ...rdfData,
          correspondingFund: { ...iClassFundDetails },
          amount,
          percent: 100
        }
      ],
      loadedData: { iClassFundDetails }
    };
  } else {
    const enablementStatus = await getEnablementStatus(appState);

    const { iClassFunds: iClassFundReward } = unlockRewards(assetTierID);

    const rdfRecommendation = isRDFPath(appState)
      ? await getRDFRecommendation({
          birthDate: dob,
          amount,
          isSummitMember: meetsSummitIClassMin(iClassFundReward, amount)
        })
      : {};

    const iClassFundDetails = isRDFPath(appState)
      ? await getRDFRecommendation({
          birthDate: dob,
          amount,
          isSummitMember: true
        })
      : {};

    res = merge(
      merge(
        {
          amount,
          app: {
            customer: parseCustomerInformation(inputs, customer, enabled)
          }
        },
        enablementStatus
      ),
      isRDFPath(appState)
        ? {
            loadedData: {
              rdfData: {
                ...rdfRecommendation,
                correspondingFund: { ...iClassFundDetails }
              }
            }
          }
        : {}
    );
  }

  return res;
};
/* eslint-enable */

export const commitMoneyMarketFund: DataLoader = async ({
  store: { amount },
  chosen
}) => {
  if (chosen === 'viewAllMoneyMarketFunds') return {};

  const { investmentMethod, fund } = chosen;
  const { ticker, trpCode, correspondingFund } = fund;

  const newInvestments = [
    {
      ticker,
      trpCode,
      fundingDetails: {
        fundingAmount: amount,
        fundingAmountPercentage: 100
      }
    }
  ];

  analyticsDataLayer({
    quantity: newInvestments.length,
    newInvestments
  });

  return {
    amount,
    app: { newInvestments, investmentMethod },
    loadedData: { moneyMarketData: { ...fund } },
    chosenInvestments: [{ ...fund, amount, percent: 100, correspondingFund }]
  };
};

// if customer chooses APP investment method, persist amount
export const commitAmount = ({ inputs }): PartialStateStore => {
  const { amount } = inputs;

  return amount
    ? {
        amount: parseInt(amount, 10)
      }
    : {};
};

export const appendMailingAddressIfDifferent = (
  addresses,
  mailingAddressDifferent,
  mailingAddress
): Array<any> => {
  if (!mailingAddressDifferent) {
    return addresses;
  }

  addresses.push(mailingAddress);

  return addresses;
};

export const populateResidentialAddress = (
  useResidentialAddress,
  originalAddress,
  correctedAddress
): any => {
  let residentialAddress = {};
  if (useResidentialAddress === 'useOriginalResidentialAddress') {
    const { street1, street2, city, state, zip } = originalAddress;
    residentialAddress = {
      street1,
      street2,
      city,
      state,
      zip,
      corrected: false
    };
  } else {
    const { street1, street2, city, state, zip, zip4 } = correctedAddress;
    residentialAddress = {
      street1,
      street2,
      city,
      state,
      zip,
      zip4,
      corrected: true
    };
  }

  return residentialAddress;
};

/* eslint-disable complexity */
// TODO: simplify complexity
export const commitAddressValidation = async ({
  inputs,
  store,
  chosen
}): Promise<StateStore> => {
  const {
    app: { customer }
  } = store;

  // If user clicks on Cancel/Close icon on the modal and open the modal again,
  // need to fetch the previously entered values from the store
  const residentialStreet1 = withDefault(
    inputs.residentialStreet1,
    store?.residentialAddressValidation?.originalAddress?.street1
  );
  const residentialStreet2 = withDefault(
    inputs.residentialStreet2,
    store?.residentialAddressValidation?.originalAddress?.street2 || ''
  );
  const residentialZip = withDefault(
    inputs.residentialZip,
    store?.residentialAddressValidation?.originalAddress?.zip
  );
  const residentialCity = withDefault(
    inputs.residentialCity,
    store?.residentialAddressValidation?.originalAddress?.city
  );
  const residentialState = withDefault(
    inputs.residentialState,
    store?.residentialAddressValidation?.originalAddress?.state
  );
  const { phone1, phone2, phone3 } = inputs;

  const mailingAddressDifferent = withDefault(
    inputs?.mailingAddressDifferent,
    chosen?.mailingAddressDiffValue
  );
  const primaryEmailAddress = withDefault(
    inputs?.primaryEmailAddress,
    customer?.primaryEmailAddress
  );

  const residentialAddress = {
    city: residentialCity.trim(),
    country: 'United States',
    state: residentialState.trim(),
    street1: residentialStreet1.trim(),
    street2: residentialStreet2?.trim(),
    street3: '',
    usaddress: true,
    zip: residentialZip.trim()
  };

  let mailingAddress = {};
  if (mailingAddressDifferent) {
    // If user clicks on Cancel/Close icon on the modal and open the modal again,
    // need to fetch the previously entered values from the store
    const mailingStreet1 = withDefault(
      inputs.mailingStreet1,
      store?.mailingAddressValidation?.originalAddress?.street1
    );
    const mailingStreet2 = withDefault(
      inputs.mailingStreet2,
      store?.mailingAddressValidation?.originalAddress?.street2 || ''
    );
    const mailingZip = withDefault(
      inputs.mailingZip,
      store?.mailingAddressValidation?.originalAddress?.zip
    );
    const mailingCity = withDefault(
      inputs.mailingCity,
      store?.mailingAddressValidation?.originalAddress?.city
    );
    const mailingState = withDefault(
      inputs.mailingState,
      store?.mailingAddressValidation?.originalAddress?.state
    );

    mailingAddress = {
      city: mailingCity.trim(),
      country: 'United States',
      state: mailingState.trim(),
      street1: mailingStreet1.trim(),
      street2: mailingStreet2?.trim(),
      street3: '',
      usaddress: true,
      zip: mailingZip.trim()
    };
  }

  let addresses = [residentialAddress];
  addresses = appendMailingAddressIfDifferent(
    addresses,
    mailingAddressDifferent,
    mailingAddress
  );

  let phones = [
    { phone: phone1, type: PhoneNumberType.CELLULAR },
    {
      phone: phone2,
      type: PhoneNumberType.DAYTIME
    },
    { phone: phone3, type: PhoneNumberType.EVENING }
  ]
    .filter(({ phone }) => phone)
    .map(({ phone, type }) => parsePhone(phone, type));

  if (phones?.length < 1) phones = customer?.phones;

  const [
    residentialAddressValid,
    mailingAddressValid
  ] = await postAddressValidation(addresses);

  const residentialAddressValidation =
    residentialAddressValid || residentialAddress;
  const mailingAddressValidation = mailingAddressValid || mailingAddress;

  // need to commit email address in the case user has updated it since basic pi
  // otherwise modal action will wipe any user updates to email address
  return {
    residentialAddressValidation,
    ...(mailingAddressDifferent && { mailingAddressValidation }),
    mailingAddressDifferent,
    app: {
      customer: {
        phones,
        primaryEmailAddress
      }
    }
  };
};
/* eslint-enable complexity */

export const commitContactInformation: DataLoader = async ({
  inputs,
  chosen,
  store
}): Promise<StateStore> => {
  if (chosen?.section === 'addressValidation') {
    return commitAddressValidation({ inputs, store, chosen });
  }

  const {
    residentialAddressValidation,
    mailingAddressValidation = {},
    mailingAddressDifferent
  } = store;

  const { useResidentialAddress, useMailingAddress } = inputs;

  const originalResidentialAddress = getOriginalResidentialAddress(
    residentialAddressValidation
  );
  const correctedResidentialAddress = getCorrectedResidentialAddress(
    residentialAddressValidation
  );
  const residentialAddress = populateResidentialAddress(
    useResidentialAddress,
    originalResidentialAddress,
    correctedResidentialAddress
  );

  const originalMailingAddress = getOriginalMailingAddress(
    mailingAddressValidation
  );
  const correctedMailingAddress = getCorrectedMailingAddress(
    mailingAddressValidation
  );
  let mailingAddress = residentialAddress;
  if (mailingAddressDifferent) {
    if (useMailingAddress === 'useOriginalMailingAddress') {
      const { street1, street2, city, state, zip } = originalMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        corrected: false
      };
    } else {
      const {
        street1,
        street2,
        city,
        state,
        zip,
        zip4
      } = correctedMailingAddress;
      mailingAddress = {
        street1,
        street2,
        city,
        state,
        zip,
        zip4,
        corrected: true
      };
    }
  }

  return {
    app: {
      customer: {
        residentialAddress,
        mailingAddress
      }
    }
  };
};

export const loadCustodians: DataLoader = async (): Promise<StateStore> => ({
  app: {},
  loadedData: {
    institutions: await getCustodiansService()
  }
});

export const loadTermsForSubmission: DataLoader = async ({
  store
}): Promise<StateStore> => {
  const { termsData } = store;
  const electronicAccessTerms = await loadElectronicAccessTerms();
  const electronicAccessDeliveryTerms = await loadElectronicAccessDeliveryTerms();
  const parsedAEMTermsContent = await loadAEMTermsContent();

  const { termsID: electronicAccessTermsID } = electronicAccessTerms;
  const {
    termsID: electronicAccessDeliveryTermsID,
    termsAndConditionParagraphs: electronicAccessDeliveryTermsParagraphs
  } = electronicAccessDeliveryTerms;
  const termsIDsAsStrings = [
    electronicAccessTermsID,
    electronicAccessDeliveryTermsID
  ].map(id => id?.toString());

  // call conversion only when on review screen, not contact information; loader is shared
  if (store?.prospect && window.location.pathname.includes('review')) {
    /* convert prospect users to RSP entity */
    await convertProspectCustomer();
  }

  return {
    app: {
      termsIDs: termsIDsAsStrings
    },
    termsData: {
      ...termsData,
      electronicAccessDelivery: {
        electronicAccessDeliveryTermsID,
        electronicAccessDeliveryTermsParagraphs,
        aemTermsContent: parsedAEMTermsContent?.value
      }
    }
  };
};

export const commitReferrerForm: DataLoader = async ({
  store,
  chosen,
  inputs
}): Promise<PartialStateStore> => {
  // Bypasses commiting to store when not needed
  if (chosen === 'guide') return { loadedData: { referrerError: false } };

  const { refFullName = '', refEmail = '' } = inputs;

  if (refFullName.trim() === '' || refEmail.trim() === '') {
    return { loadedData: { referrerError: true } };
  }

  const {
    app: { customer }
  } = store;

  if (chosen === 'referrer') {
    const customerFullName = `${customer?.name?.firstName} ${customer?.name?.lastName}`;
    const refPayload = {
      referrerName: refFullName,
      referrerEmailAddress: refEmail,
      referredName: customerFullName,
      referredEmailAddress: customer?.primaryEmailAddress
    };

    await postReferrer(refPayload);
  }

  return {
    loadedData: { submittedReferrer: true, referrerError: false }
  };
};

export const loadPlaidConfig: DataLoader = async ({ store }) => {
  const {
    app: { profileApplicationID = '' }
  } = store;
  const plaidConfig = await getPlaidLinkToken(profileApplicationID);

  return {
    loadedData: {
      config: {
        ...plaidConfig,
        // eslint-disable-next-line camelcase
        token: plaidConfig?.plaidLinkTokenCreateResponse?.link_token
      }
    }
  };
};

export const commitProgressStepText: DataLoader = ({ store }) => {
  const { progressStepText: lastProgressStepText } = store;

  const progressStepText = [lastProgressStepText, currentRouteToHash()]
    .filter(truthy => truthy)
    .join(',');

  const storeToCommit = { ...store, progressStepText };
  return {
    ...storeToCommit
  };
};

// @Abhinav - fix complexity
/* eslint-disable complexity */
export const pollForSubmissionResponse: DataLoader = async (
  trackingIdentifier
): Promise<PartialStateStore> => {
  // throw error if no trackingIdentifier
  // as initial call for submission has failed; no polling necessary
  if (!trackingIdentifier) {
    throw new ServiceError('Initial submission failure; no tracking id...');
  }

  // possible submission error codes, we can add more if need be
  const submissionErrorCodes = [204, 400, 401, 402, 403, 404, 500, 503];
  let intervalId;
  let applicationStatus = applicationStatusTypes.IN_PROGRESS;

  /* START - submission polling logic */
  const loadedData = await new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      // clears the interval/timeout if no response from service in 100s
      // and sets the timeout flag to true
      // marks application complete
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      applicationStatus = applicationStatusTypes.COMPLETED;
      resolve({
        is504Timeout: true
      });
    }, 100000);

    // sets an interval of 5s to poll the submission tracking endpoint
    intervalId = setInterval(async () => {
      const submissionTrackingPayload = { trackingIdentifier };
      const {
        nasuResponse,
        progress: creationProgress
      } = await submissionTrackingService(submissionTrackingPayload);

      // once the submission is successful, clears the interval/timeout and
      // mark the app as complete
      if (
        nasuResponse?.responseCode !== null &&
        nasuResponse?.responseCode?.httpResponseCode === 200 &&
        creationProgress?.Creation === 'Complete'
      ) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        applicationStatus = applicationStatusTypes.COMPLETED;
        resolve({
          ...nasuResponse
        });
      }

      // account for 504 response code from result endpoint and set is504Timeout flag to true
      // also clear the interval/timeout and mark the app as complete
      else if (
        nasuResponse?.responseCode !== null &&
        nasuResponse?.responseCode?.httpResponseCode === 504
      ) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        applicationStatus = applicationStatusTypes.COMPLETED;
        resolve({
          is504Timeout: true
        });
      }

      // take into account 202s (switch to check) as it's not a true error but returns a responseCode.
      // clears the interval/timeout and mark the app as complete
      else if (
        nasuResponse?.responseCode !== null &&
        nasuResponse?.responseCode?.httpResponseCode === 202 &&
        isSwitchToCheck(nasuResponse)
      ) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        applicationStatus = applicationStatusTypes.COMPLETED;
        resolve({
          ...nasuResponse
        });
      }

      // throws error if there are any service failures and
      // clears the interval/timeout
      else if (
        nasuResponse?.responseCode !== null &&
        submissionErrorCodes.includes(
          nasuResponse?.responseCode?.httpResponseCode
        )
      ) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        reject(
          new ServiceError(
            `A service call has failed with status: ${nasuResponse?.responseCode?.httpResponseCode}`
          )
        );
      }
    }, 5000);
    /* END - submission polling logic */
  });

  return { loadedData, app: { applicationStatus } };
};
/* eslint-enable complexity */

export const commitShoppingFunds: DataLoader = async ({
  chosen
}): Promise<StateStore> => {
  const shoppingFunds = chosen;

  const storeToCommit = {
    app: {},
    ...(shoppingFunds && { loadedData: { shoppingFunds } })
  };

  return {
    ...storeToCommit
  };
};

/**
 * Function which invokes nitro auth service
 * to fetch user session authentication details
 * @returns {object}
 * */
export const initiateAuthService = async (): Promise<any> => {
  const authService = newAuthService({
    apiKey: config('apiKey'),
    types: {
      external: {
        apiKey: config('apiKey'),
        requireAuthentication: false
      }
    }
  });

  const authState = await authService?.verifyAuthentication();
  return authState;
};

export const loadMoneyMarketData: DataLoader = async (): Promise<PartialStateStore> => {
  const moneyMarketFundData = await getMoneyMarketFunds();

  return {
    loadedData: { moneyMarketFundData }
  };
};
